import {useEffect, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {ClipboardCheck, ListChecks} from 'lucide-react';
import {
  Badge,
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
  Spinner,
  Tabs,
  TabsList,
  TabsTrigger,
  ToggleGroup,
  ToggleGroupItem
} from '@kroll-resolver-crisp/ui-components-react';
import {useQaPolicyViolations, useQaReviewItems, useClassificationConfig} from '@/api/hooks';
import {type QaReviewItem} from '@/api/qaReview';
import {FilterBarSlotProvider} from '@/components/layout/FilterBarSlot';
import {SensitiveMediaToggle} from '@/components/shared/SensitiveMedia';
import {QaQueueTable} from '@/components/weeklyQa/QaQueueTable';
import {QaReviewModal} from '@/components/weeklyQa/QaReviewModal';
import {QaAccuracySummary} from '@/components/weeklyQa/QaAccuracySummary';
import {QaConfigAdmin} from '@/components/weeklyQa/QaConfigAdmin';
import {isPolicyViolationType} from '@/lib/classificationType';
import {TablePanel} from '@/components/ui/table-panel';
import {showToast} from '@/lib/toast';

type Section = 'queue' | 'accuracy' | 'configuration';

const isSection = (value: string | null): value is Section =>
  value === 'queue' || value === 'accuracy' || value === 'configuration';

// Matches the page size requested below. The backend returns a bare page with no total-count
// envelope, so "is there a next page" is inferred from whether this page came back full.
const QUEUE_PAGE_SIZE = 50;

const SECTIONS: Array<{value: Section; label: string}> = [
  {value: 'queue', label: 'Queue'},
  {value: 'accuracy', label: 'Accuracy'},
  {value: 'configuration', label: 'Configuration'}
];

export function WeeklyQaPage() {
  // The tab, vertical and queue page live in the URL, so a reload or a shared link lands on the
  // same list.
  const [searchParams, setSearchParams] = useSearchParams();
  const rawSection = searchParams.get('tab');
  const section: Section = isSection(rawSection) ? rawSection : 'queue';
  const selectedVertical = searchParams.get('vertical');
  const queuePage = Math.max(Number(searchParams.get('page')) || 1, 1);
  // The filter bar's slot, where the Accuracy and Configuration tabs portal their own controls.
  const [filterSlot, setFilterSlot] = useState<HTMLDivElement | null>(null);
  const [reviewItem, setReviewItem] = useState<QaReviewItem | null>(null);
  // Snapshot of the pending queue for a "Review All" run, plus the current position in it. Kept
  // separate from the live `queueItems` query so items don't reorder/disappear mid-flow as
  // submissions invalidate that query out from under the reviewer.
  const [reviewQueue, setReviewQueue] = useState<QaReviewItem[] | null>(null);
  const [reviewIndex, setReviewIndex] = useState(0);

  const {data: verticalSummaries = [], isLoading: loadingVerticals} = useQaPolicyViolations();
  const classifications = useClassificationConfig();
  const configVerticals = (classifications.data ?? [])
    .filter((c) => isPolicyViolationType(c.type))
    .map((c) => c.name);

  const activeVertical = selectedVertical ?? verticalSummaries[0]?.policy_violation ?? null;

  const {data: queueItems, isLoading: loadingQueue} = useQaReviewItems(
    activeVertical
      ? {
          policy_violation: activeVertical,
          status: 'pending',
          page: queuePage,
          page_size: QUEUE_PAGE_SIZE
        }
      : undefined
  );

  const hasNextQueuePage = (queueItems?.length ?? 0) === QUEUE_PAGE_SIZE;

  // Reviewing the last item on a page (or switching vertical) can leave `queuePage` pointing past
  // the end of the now-shorter queue - fall back a page rather than showing a stuck "empty" table.
  useEffect(() => {
    if (!loadingQueue && queuePage > 1 && (queueItems?.length ?? 0) === 0) {
      setQueuePage(queuePage - 1);
    }
    // The setQueuePage call reads the current params; only the check's inputs are tracked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingQueue, queueItems, queuePage]);

  // Writes a change into the URL without a history entry, dropping back to page 1 for anything but
  // paging itself.
  function update(change: (next: URLSearchParams) => void, keepPage = false) {
    const next = new URLSearchParams(searchParams);
    change(next);
    if (!keepPage) {
      next.delete('page');
    }

    setSearchParams(next, {replace: true});
  }

  function setSection(value: Section) {
    update((next) => {
      if (value === 'queue') {
        next.delete('tab');
      } else {
        next.set('tab', value);
      }
    });
  }

  function setQueuePage(page: number) {
    update((next) => {
      if (page > 1) {
        next.set('page', String(page));
      } else {
        next.delete('page');
      }
    }, true);
  }

  function selectVertical(vertical: string) {
    update((next) => {
      next.set('vertical', vertical);
    });
  }

  function startReviewAll() {
    const pending = queueItems ?? [];
    if (pending.length === 0) {
      return;
    }

    setReviewQueue(pending);
    setReviewIndex(0);
    setReviewItem(pending[0] ?? null);
  }

  function handleReviewSubmitted() {
    if (reviewQueue) {
      const nextIndex = reviewIndex + 1;
      if (nextIndex < reviewQueue.length) {
        setReviewIndex(nextIndex);
        setReviewItem(reviewQueue[nextIndex] ?? null);
        return;
      }

      showToast(`Reviewed all ${reviewQueue.length} items.`);
      setReviewQueue(null);
    } else {
      showToast('Review submitted.');
    }

    setReviewItem(null);
  }

  function handleReviewOpenChange(open: boolean) {
    if (!open) {
      setReviewQueue(null);
      setReviewItem(null);
    }
  }

  const showVerticals = section === 'queue' && !loadingVerticals && verticalSummaries.length > 0;

  return (
    <div className="text-foreground flex h-full w-full flex-col">
      {/* Tabs and every control for the active tab sit in one white bar over the page's grey, as
          on Submissions and QC Review, so the controls read apart from the tables. */}
      <div className="bg-card border-border shrink-0 space-y-3 border-b px-5 py-3">
        <Tabs
          value={section}
          onValueChange={(value) => {
            setSection(value as Section);
          }}>
          <TabsList>
            {SECTIONS.map((s) => (
              <TabsTrigger key={s.value} value={s.value}>
                {s.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="flex flex-wrap items-center gap-2">
          {showVerticals ? (
            <ToggleGroup
              variant="outline"
              spacing={0}
              size="sm"
              aria-label="Vertical"
              className="flex-wrap"
              value={activeVertical ? [activeVertical] : []}
              onValueChange={(next) => {
                const vertical = next[0];
                if (vertical) {
                  selectVertical(vertical);
                }
              }}>
              {verticalSummaries.map((v) => {
                const selected = v.policy_violation === activeVertical;
                return (
                  <ToggleGroupItem
                    key={v.policy_violation}
                    value={v.policy_violation}
                    disabled={v.pending === 0 && !selected}>
                    {v.policy_violation}
                    <Badge variant={selected ? 'default' : 'secondary'}>{v.pending}</Badge>
                  </ToggleGroupItem>
                );
              })}
            </ToggleGroup>
          ) : null}

          <div ref={setFilterSlot} className="flex min-w-0 flex-1 flex-wrap items-center gap-2" />

          {showVerticals ? (
            <Button
              type="button"
              size="sm"
              disabled={(queueItems?.length ?? 0) === 0}
              onClick={startReviewAll}>
              <ListChecks />
              Review all{queueItems?.length ? ` (${queueItems.length})` : ''}
            </Button>
          ) : null}

          {/* How the tables' media is shown, so it sits with the view controls rather than in the
              page header, as on Investigations. */}
          <SensitiveMediaToggle />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        <FilterBarSlotProvider value={filterSlot}>
          {section === 'queue' ? (
            loadingVerticals ? (
              <div className="text-muted-foreground flex h-32 items-center justify-center gap-2 text-xs">
                <Spinner />
                Loading verticals…
              </div>
            ) : verticalSummaries.length === 0 ? (
              <Empty variant="outline" className="py-10">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <ClipboardCheck />
                  </EmptyMedia>
                  <EmptyTitle>No Weekly QA verticals</EmptyTitle>
                  <EmptyDescription>
                    Once a Weekly QA config fires for a vertical, sampled items will show up here.
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            ) : (
              <div className="flex flex-col gap-4">
                <TablePanel>
                  <QaQueueTable
                    items={queueItems ?? []}
                    isLoading={loadingQueue}
                    onOpenReview={(item) => {
                      setReviewQueue(null);
                      setReviewItem(item);
                    }}
                  />
                </TablePanel>

                {queuePage > 1 || hasNextQueuePage ? (
                  // No numbered links: the backend gives no total, so only the current page is
                  // known. With no href the previous/next links render as buttons, which is what
                  // client-side paging wants. The package types their props as an anchor's, which
                  // has no `disabled`, so it is spread in rather than written as an attribute.
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          {...{disabled: queuePage <= 1 || loadingQueue}}
                          onClick={() => {
                            setQueuePage(Math.max(1, queuePage - 1));
                          }}
                        />
                      </PaginationItem>
                      <PaginationItem>
                        <span className="text-muted-foreground px-2 text-xs">Page {queuePage}</span>
                      </PaginationItem>
                      <PaginationItem>
                        <PaginationNext
                          {...{disabled: !hasNextQueuePage || loadingQueue}}
                          onClick={() => {
                            setQueuePage(queuePage + 1);
                          }}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                ) : null}
              </div>
            )
          ) : null}

          {section === 'accuracy' ? <QaAccuracySummary /> : null}

          {section === 'configuration' ? <QaConfigAdmin verticals={configVerticals} /> : null}
        </FilterBarSlotProvider>
      </div>

      <QaReviewModal
        item={reviewItem}
        queuePosition={
          reviewQueue ? {current: reviewIndex + 1, total: reviewQueue.length} : undefined
        }
        onOpenChange={handleReviewOpenChange}
        onSubmitted={handleReviewSubmitted}
      />
    </div>
  );
}
